<script lang="ts">
	import { readable } from 'svelte/store';
	import { fade, fly } from 'svelte/transition';
	import { twMerge } from 'tailwind-merge';
	import type { DialogStores } from '../Dialog/utils/createDialogStores';

	let {
		classes = {},
		drawerStores,
		dataCy,
		children,
		allowScroll,
		shouldNotClose,
		onClose,
		ariaLabel,
		ariaLabelledBy,
		ariaDescribedBy,
		...rest
	}: Props = $props();

	interface Props {
		classes?: { overlay?: string };
		drawerStores: DialogStores;
		dataCy: string;
		class?: string;
		allowScroll?: boolean;
		shouldNotClose?: boolean;
		/** Called on an overlay click, before the drawer closes (unless `shouldNotClose`). */
		onClose?: () => void;
		ariaLabel?: string;
		ariaLabelledBy?: string;
		ariaDescribedBy?: string;
		children?: import('svelte').Snippet;
	}

	let contentClasses = $derived(
		twMerge(
			'bg-gray-0 fixed right-0 top-0 bottom-0 z-50 w-full shadow-lg max-w-[350px] focus:outline-none',
			rest.class
		)
	);

	let open = $derived(drawerStores?.states.open);
	// Fall back to an inert closed store when no drawerStores is provided, so the
	// `$isOpen` subscription never runs against `undefined`.
	let isOpen = $derived(open ?? readable(false));

	$effect(() => {
		if (!allowScroll) {
			document.body.style.overflow = $isOpen ? 'hidden' : 'scroll';
		}
	});
</script>

<div data-cy={`${dataCy}-drawer`}>
	{#if $isOpen}
		<button
			type="button"
			aria-label="Close drawer"
			onclick={() => {
				onClose?.();
				if (!shouldNotClose) {
					open?.set(false);
				}
			}}
			class={twMerge('fixed inset-0 z-50 bg-black/50', classes.overlay)}
			transition:fade={{ duration: 150 }}
		></button>
		<div
			class={contentClasses}
			role="dialog"
			aria-modal="true"
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledBy}
			aria-describedby={ariaDescribedBy}
			transition:fly={{
				x: 500,
				duration: 300,
				opacity: 1
			}}
		>
			{@render children?.()}
		</div>
	{/if}
</div>
